import { ApiDimension, ApiMetric, ApiOperator } from "@incendium/api";
import { Box } from "@mui/material";
import { AttributionMetric } from "features/analytics/types/types";
import { metricsByAttribution } from "features/analytics/services/attributionMapping";
import { CampaignAnalysisAnalyzeFilters } from "features/attributionAnalytics";
import {
  ExplorerProvider,
  ExplorerSidebar,
  ExplorerTable,
  useExplorerContext,
} from "features/explorer";
import ExplorerTopBar from "features/explorer/components/ExplorerTopBar";
import { TExploreConfig } from "features/explorer/types";
import { useCallback, useMemo, useState } from "react";

function UtmExplorerPageInner() {
  const [open, setOpen] = useState(false);
  const { attributes, setAttributes } = useExplorerContext();

  const selectedPlatforms = useMemo(() => {
    return attributes
      .filter((a) => a.key === ApiDimension.DIMENSION_SOURCE)
      .map((a) => a.value);
  }, [attributes]);
  const selectedCampaigns = useMemo(() => {
    return attributes
      .filter((a) => a.key === ApiDimension.DIMENSION_CAMPAIGN)
      .map((a) => a.value);
  }, [attributes]);
  const selectedGroups = useMemo(() => {
    return attributes
      .filter((a) => a.key === ApiDimension.DIMENSION_CONTENT)
      .map((a) => a.value);
  }, [attributes]);
  const selectedKeywords = useMemo(() => {
    return attributes
      .filter((a) => a.key === ApiDimension.DIMENSION_TERM)
      .map((a) => a.value);
  }, [attributes]);

  const setSelectedFilters = useCallback(
    (filter: ApiDimension) => (value) => {
      setAttributes((att) => [
        ...att.filter((a) => a.key !== filter),
        ...value.map((v) => ({
          key: filter,
          value: v,
          operator: ApiOperator.CONTAINS,
        })),
      ]);
    },
    [setAttributes],
  );

  const metrics = useMemo(() => {
    return [
      ApiMetric.METRIC_SPEND,
      ApiMetric.METRIC_PUBLISHER_CPC,
      ApiMetric.METRIC_PUBLISHER_CLICKS,
      ApiMetric.METRIC_SESSIONS_COUNT,
      ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
      ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
      ApiMetric.METRIC_MACRO_CONVERSION_RATE,
      ApiMetric.METRIC_REVENUE,
      ApiMetric.METRIC_ROAS,
    ];
  }, []);

  const groupedMetrics = useMemo(() => {
    return [
      {
        name: "Publisher Data",
        metrics: [
          ApiMetric.METRIC_SPEND,
          ApiMetric.METRIC_PUBLISHER_CPC,
          ApiMetric.METRIC_PUBLISHER_CLICKS,
        ],
      },
      {
        name: "Traffic & Engagement",
        metrics: [
          ApiMetric.METRIC_SESSIONS_COUNT,
          ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
        ],
      },
      {
        name: "Attribution & Conversion",
        metrics: [
          ...metricsByAttribution(AttributionMetric.MACRO),
          ...metricsByAttribution(AttributionMetric.MACRO_RATE),
          ...metricsByAttribution(AttributionMetric.REVENUE),
          ...metricsByAttribution(AttributionMetric.ROAS),
        ],
      },
    ];
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        gap: 2,
      }}
    >
      <ExplorerTopBar
        onOpenSidebar={() => setOpen(true)}
        leftSlot={
          <Box sx={{ width: 700 }}>
            <CampaignAnalysisAnalyzeFilters
              disablePaidChannelAttributes={true}
              selectedPlatforms={selectedPlatforms}
              setSelectedPlatforms={setSelectedFilters(
                ApiDimension.DIMENSION_SOURCE,
              )}
              selectedCampaigns={selectedCampaigns}
              setSelectedCampaigns={setSelectedFilters(
                ApiDimension.DIMENSION_CAMPAIGN,
              )}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedFilters(
                ApiDimension.DIMENSION_CONTENT,
              )}
              selectedKeywords={selectedKeywords}
              setSelectedKeywords={setSelectedFilters(
                ApiDimension.DIMENSION_TERM,
              )}
              paidChannelAttributes={[]}
              filterLabels={{
                [ApiDimension.DIMENSION_SOURCE]: "Select UTM Source",
                [ApiDimension.DIMENSION_CAMPAIGN]: "Select UTM Campaign",
                [ApiDimension.DIMENSION_CONTENT]: "Select UTM Content",
                [ApiDimension.DIMENSION_TERM]: "Select UTM Term",
              }}
            />
          </Box>
        }
      />

      <Box sx={{ minHeight: 0, flexGrow: 1 }} pb={2}>
        <ExplorerTable
          metrics={metrics}
          groupedMetrics={groupedMetrics}
          dontIncludeEmpty
          colWidths={
            {
              [ApiDimension.DIMENSION_SOURCE]: 120,
              [ApiDimension.DIMENSION_MEDIUM]: 100,
            } as { [key: string]: number }
          }
          extraDimensions={[
            ApiDimension.DIMENSION_CAMPAIGN,
            ApiDimension.DIMENSION_MEDIUM,
            ApiDimension.DIMENSION_CONTENT,
            ApiDimension.DIMENSION_TERM,
          ]}
        />
      </Box>
      <ExplorerSidebar
        open={open}
        onClose={() => setOpen(false)}
        filterTitle="Filter UTM"
      />
    </Box>
  );
}

function UtmExplorerPage() {
  const config: TExploreConfig<"utm"> = useMemo(
    () => ({
      utm: {
        title: "",
        desc: ``,
        type: "top",
        ranking: [],

        analyseBy: {
          description: "",
          grouping: [
            {
              option: "Platform",
              dimensions: [ApiDimension.DIMENSION_SOURCE],
            },
          ],
        },
      },
    }),
    [],
  );
  return (
    <ExplorerProvider configs={config} type={"utm"}>
      <UtmExplorerPageInner />
    </ExplorerProvider>
  );
}

export default UtmExplorerPage;
